import { h, Component, Prop, Host, Watch, Element } from '@stencil/core';

import { isNumber, parsePropToArray, getCSSVariable } from '../../../services/utils';
import { isString } from '../../../services/utils/isString';

@Component({
  tag: 'pk-progress-indicator',
  styleUrl: './scss/pk-progress-indicator.scss',
  shadow: true,
})
export class PkProgressIndicator {
  // Own Properties
  // ====================

  private cssComponentPrefix = `--progress-indicator`;
  private arrayColors: string[];
  private arrayPercentages: number[];

  // Reference to host HTML element
  // ===================================
  @Element() el!: HTMLPkProgressIndicatorElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If true, the animation will be deactivated */
  @Prop({ reflect: true }) animationOff: boolean = false;

  /** Specify the animation shorthand CSS to apply.
   * Details: https://developer.mozilla.org/en-US/docs/Web/CSS/animation */
  @Prop({ reflect: true }) animationEffect: string;

  /** Background color of the circular inner area (ignored if shape="linear") */
  @Prop({ reflect: true }) innerAreaColor: string;

  /** Will define the shape to be used at the beginnig/end of track area and progress bar */
  @Prop({ reflect: true }) linecap: 'round' | 'square' = 'round';

  /** Value of the progress line tracking in a range of 1 - 100 (if one stat).
   * Use an Array for multiple stats */
  @Prop({ reflect: true }) percentages: string | number | number[];

  /** Background color of the progress area element (only for circular)*/
  @Prop({ reflect: true }) progressColor: string;

  /** Width and height size of the circular progress indicator (ignored if shape="linear") */
  @Prop({ reflect: true }) size: number;

  /** Thickness value of the progress area.
   * It will set the height for linear shape type and the stroke width for circular shape type. */
  @Prop({ reflect: true, mutable: true }) stroke: number;

  /** Shape type of the progress indicator */
  @Prop({ reflect: true }) shape: 'linear' | 'circular' = 'linear';

  /** Background color of the tracking area element */
  @Prop() trackAreaColor: string;

  /** Specify the colors for each bar. The colors property expects a single array or a string. */
  @Prop({ reflect: true }) colors: string | string[];

  /** Specify the distance between bars (multiple bars) in px. If 0 is sent there will be no spacing and the border radius will be 0 */
  @Prop({ reflect: true }) barSpacing: number;

  // Prop lifecycle events
  // =======================

  @Watch('stroke')
  handleStrokeProp() {
    if (this.stroke) return;
    this.stroke = this.shape === 'linear' ? 10 : 4;
  }

  @Watch('colors')
  updateColors() {
    this.setProgressBarColor();
  }

  @Watch('percentages')
  updatePercentage() {
    this.setPercentagesArray();
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.handleStrokeProp();
    this.setProgressBarColor();
    this.setPercentagesArray();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private setPercentage = (): number[] => {
    if (isNumber(this.percentages)) return;

    try {
      const parsedPercentagesArray: number[] = isString(this.percentages)
        ? parsePropToArray(this.percentages)
        : this.percentages;
      const sum = parsedPercentagesArray.reduce((partialSum, a) => partialSum + a, 0);

      return parsedPercentagesArray.map((percentage) => (percentage * 100) / sum);
    } catch (error) {
      console.warn(
        '[Panenka:<pk-progress-indicator>] ❌: please make sure the percentage value is a valid number or array of numbers',
        error,
      );
    }
  };

  private setPercentagesArray = () => {
    this.arrayPercentages = [];
    if (isNumber(this.percentages)) {
      this.arrayPercentages.push(this.percentages);
      return;
    }

    this.arrayPercentages = this.setPercentage();
  };

  private setProgressDiv = (percent?: number, index?: number) => {
    if (percent === 0) return null;

    const bgColor = index ? this.arrayColors[index] : this.arrayColors[0];

    return (
      <div
        style={{
          width: `${percent}%`,
          'background-color': bgColor,
        }}
        class={{
          'progress-bar': true,
          'no-space': this.barSpacing === 0,
          [`linecap-${this.linecap}`]: true,
          'animation-on': !this.animationOff && isNumber(this.percentages),
        }}
        part="progress-bar"
      />
    );
  };

  private setProgressBarColor = () => {
    this.arrayColors = [];
    if (!this.colors) {
      this.arrayColors.push('var(--pk-data-01)');
      return;
    }

    try {
      const colors: string[] = parsePropToArray(this.colors);
      this.arrayColors = colors.map((color: string) => `var(--pk-${color})`);
    } catch {
      this.arrayColors.push(`var(--pk-${this.colors})`);
    }
  };

  get circularPercentage() {
    if (!isNumber(this.percentages) || isNumber(this.percentages < 0)) return 0;

    return this.percentages;
  }
  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(isNumber(this.percentages) && {
        [`${this.cssComponentPrefix}--linear-percentage`]: `${this.percentages}%`,
      }),
      ...(this.animationEffect && {
        [`${this.cssComponentPrefix}--linear-animation`]: this.animationEffect,
      }),
      // Apply styles for circular progress indicator
      ...(this.shape === 'circular' && {
        ...(this.animationEffect && {
          [`${this.cssComponentPrefix}--circular-animation`]: this.animationEffect,
        }),
        ...(this.size && { [`${this.cssComponentPrefix}--circular-size`]: `${this.size}px` }),
        ...(this.innerAreaColor && {
          [`${this.cssComponentPrefix}--circular-inner-bg`]: getCSSVariable(this.innerAreaColor),
        }),
        ...(this.progressColor && {
          [`${this.cssComponentPrefix}--progress-color`]: getCSSVariable(this.progressColor),
        }),
      }),
      // Apply common styles
      ...(this.stroke && { [`${this.cssComponentPrefix}--stroke-width`]: `${this.stroke}px` }),
      ...(this.trackAreaColor && {
        [`${this.cssComponentPrefix}--track-area-color`]: getCSSVariable(this.trackAreaColor),
      }),
      ...(this.barSpacing && {
        [`${this.cssComponentPrefix}--spacing`]: `${this.barSpacing}px`,
      }),
    };

    return (
      <Host style={{ ...style }}>
        <div class={{ 'progress-indicator': true, [`${this.shape}`]: true }} part="base">
          {/* LINEAR */}
          {this.shape === 'linear' && (
            <div class={{ wrapper: true, [`linecap-${this.linecap}`]: true }} part="wrapper">
              {this.arrayPercentages?.map((percent, index) => this.setProgressDiv(percent, index))}
            </div>
          )}
          {/* CIRCULAR */}
          {this.shape === 'circular' && (
            <div part="wrapper">
              <svg viewBox={`0 0 ${this.size} ${this.size}`} class="circle">
                <circle class="track-area" cx="50%" cy="50%" part="track-area" />
                <circle
                  class={{ progress: true, [`linecap-${this.linecap}`]: true, 'animation-on': !this.animationOff }}
                  stroke-dasharray={`${this.circularPercentage} 100`}
                  pathLength={100}
                  part="circle"
                  cx="50%"
                  cy="50%"
                />
              </svg>
              <div class="circular__text" part="circular-text">
                <slot />
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
